import { useEffect, useMemo, useState } from 'react';
import { 
  Alert, 
  Box, 
  Chip, 
  LinearProgress, 
  Pagination, 
  Paper, 
  Snackbar, 
  SnackbarCloseReason, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableFooter, 
  TableHead, 
  TableRow,
  Typography,
  useTheme,
  alpha
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FerramentasDaListagemCadastros } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { IListagemTransportadoras, TransportadoraService } from '../../../shared/services/api/cadastros/TransportadoraService';

export const ListagemDeTransportadoras: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [rows, setRows] = useState<IListagemTransportadoras[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');  

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  
  // Extrai os parâmetros de busca
  const tipo = useMemo(() => searchParams.get('tipo') || 'nome', [searchParams]);
  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);
  
  useEffect(() => {
    setIsLoading(true);
    
    debounce(() => {
      TransportadoraService.getAll(pagina, tipo, filter)
        .then((result) => {
          setIsLoading(false);
          
          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados. Tente novamente mais tarde.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, tipo, debounce]);
  
  // Função para formatar CPF/CNPJ
  const formatarDocumento = (documento: string) => {
    if (!documento) return '';
    
    const apenasNumeros = documento.replace(/\D/g, '');
    
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    return documento;
  };

  return (
    <LayoutBaseDePagina
      titulo='Listagem de Transportadoras'
      barraDeFerramentas={
        <FerramentasDaListagemCadastros
          mostrarInputBusca
          mostrarInputBasicSelect
          textoDaBusca={filter}
          textoDoSelect={tipo}
          textoBotaoNovo='Nova Transportadora'
          aoClicarEmNovo={() => navigate('/transportadoras/novo')}
          aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo, pagina: '1' }, { replace: true })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ filter, tipo: textoselect, pagina: '1' }, { replace: true })}
          dadosSelecao={[
            { nameBD: 'nome', descricao: 'Nome / Razão Social' },
            { nameBD: 'cpf', descricao: 'CPF / CNPJ' },
            { nameBD: 'fantasia', descricao: 'Apelido / Nome Fantasia' },
          ]} 
        />
      }
    >
      {/* Notificação */}
      <Snackbar 
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
        open={openMsg}  
        autoHideDuration={6000} 
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={msgType}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>

      {/* Container principal com sombra suave e bordas arredondadas */}
      <Paper 
        elevation={1} 
        sx={{ 
          borderRadius: 2, 
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                '& th': { 
                  fontWeight: 'bold', 
                  color: theme.palette.primary.dark,
                  fontSize: '0.95rem',
                  py: 2
                }
              }}>
                <TableCell>Nome/Razão Social</TableCell>
                <TableCell>CPF/CNPJ</TableCell>
                <TableCell>Nome Fantasia / Apelido</TableCell>
                <TableCell align='center' sx={{ width: 120 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            
            <TableBody>
              {rows.map((row) => (
                <TableRow 
                  key={row.idtransportadora} 
                  hover
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    },
                    '&:last-child td': { borderBottom: 0 }
                  }}
                  onClick={() => navigate(`/transportadoras/${row.idtransportadora}`)}
                >
                  <TableCell>
                    <Typography variant='body1' fontWeight='medium'>
                      {row.nome}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ fontFamily: 'Monospace' }}>
                      {formatarDocumento(row.cpf)}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {row.fantasia || (
                      <Typography variant='body2' color='text.secondary' fontStyle='italic'>
                        Não informado
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell align='center'>
                    <Chip 
                      label='Ativo' 
                      size='small' 
                      color='success' 
                      variant='outlined'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
  
            {/* Mensagem de lista vazia */}
            {totalCount === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                    <Box>
                      <Typography variant='h6' color='text.secondary' gutterBottom>
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Nova Transportadora" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            
            <TableFooter>
              {/* Loading indicator */}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              
              {/* Paginação */}
              {(totalCount > 0 && totalCount > Environment.LIMITE_DE_CADASTRO) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 2 }}>
                    <Box display='flex' justifyContent='center' alignItems='center' px={2}>
                      <Pagination
                        page={pagina}
                        count={Math.ceil(totalCount / Environment.LIMITE_DE_CADASTRO)}
                        onChange={(_, newPage) => setSearchParams(
                          { filter, tipo, pagina: newPage.toString() }, 
                          { replace: true }
                        )}
                        color='primary'
                        shape='rounded'
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};